import {AfterViewInit, ChangeDetectionStrategy, Component, inject, signal} from '@angular/core';
import {PortfolioApi, PortfolioResponse, RefdataApi} from '../../../api-client-kt/src';
import {WjNavModule, WjTab, WjTabPanel} from '@mescius/wijmo.angular2.nav';
import {WjGaugeModule} from '@mescius/wijmo.angular2.gauge';
import {PortfolioSegmentDataContext} from '../../data/portfolio-segment-data-context.service';
import {groupBy, range} from 'es-toolkit';
import {IntersectionObserverDirective} from '../../directives/intersection-observer.directive';
import {AppConstants} from '../../app.constants';
import {AppPortfolioCardComponent} from '../../components/app-portfolio-card/app-portfolio-card.component';

class GroupedUwyPortfolio {
    planning = signal<PortfolioResponse[]>([]);
    tiering = signal<PortfolioResponse[]>([]);
    tracking = signal<PortfolioResponse[]>([]);
}

interface YearlyGroups {
    [year: number]: GroupedUwyPortfolio | undefined;
}

@Component({
    selector: 'app-portfolio-archive',
    imports: [WjGaugeModule, WjNavModule, IntersectionObserverDirective, AppPortfolioCardComponent],
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './portfolio-archive.component.html',
    styleUrl: './portfolio-archive.component.scss'
})
export class PortfolioArchiveComponent implements AfterViewInit {

    private readonly portfolioApi = inject(PortfolioApi);
    private readonly refdataApi = inject(RefdataApi);

    private currentYear = (new Date()).getFullYear();
    protected readonly uwYears = signal<number[]>([]);
    protected readonly selectedTabIndex = signal<number>(-1);

    private readonly loadedYearlyGroups: Record<number, GroupedUwyPortfolio | undefined> = {};

    protected readonly pfSegData = inject(PortfolioSegmentDataContext);

    constructor() {
    }

    private async loadUwYears() {
        const maxCutoff = this.currentYear + 2;
        const minCutoff = 2020;

        const uwyResponse = await this.refdataApi.getUnderwritingYears();
        const yearsInArchive = uwyResponse.map(uwy => uwy.uwyear)
            .filter(uwyear => uwyear >= minCutoff && uwyear <= maxCutoff);

        const uwMin = Math.min(...yearsInArchive);
        const uwMax = Math.max(...yearsInArchive, this.currentYear);
        const uwYears = range(uwMin, uwMax + 1).reverse();
        this.uwYears.set(uwYears);
        this.selectedTabIndex.set(uwYears.findIndex(uwy => uwy === AppConstants.getSelectedPortfolioArchiveUwy()));
    }

    async ngAfterViewInit(): Promise<void> {
        await this.loadUwYears();
    }

    protected yearlyGroups(uwyear: number): GroupedUwyPortfolio {
        if (!this.loadedYearlyGroups[uwyear]) {
            this.loadedYearlyGroups[uwyear] = new GroupedUwyPortfolio();
        }
        return this.loadedYearlyGroups[uwyear]!;
    }

    protected async onYearTabIntersection(uwyear: number) {
        const groups = this.yearlyGroups(uwyear);
        const portfoliosQueryResponse = await this.portfolioApi.queryPortfolios({ uwyear: uwyear });
        const portfoliosInUwyByPhase = groupBy(portfoliosQueryResponse, p => p.portfolioModel.phase);
        groups.planning.set(portfoliosInUwyByPhase.Planning ?? []);
        groups.tiering.set(portfoliosInUwyByPhase.Tiering ?? []);
        groups.tracking.set(portfoliosInUwyByPhase.Tracking ?? []);
    }

    protected onSelectedTabIndexChanged(tabPanel: WjTabPanel) {
        const selectedTab = tabPanel.selectedTab as WjTab;
        if (selectedTab == null) return;
        const selectedUwy = Number(selectedTab.header.innerText.trim());
        AppConstants.setSelectedPortfolioArchiveUwy(selectedUwy);
    }
}
